/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useFeatureCategoryForm.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing Feature Category form state and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createFeatureCategory, updateFeatureCategory } from '../api/featureCategoriesApi';
import { FormState, ValidationErrors } from '../types';
import { validateFeatureCategoryForm } from '../utils/validators';
import { getDefaultFormState } from '../utils/formDefaults';

/**
 * Hook for Feature Category form management
 */
export function useFeatureCategoryForm(
  initialFormData: FormState | null,
  initialSelectedFeatureIds: Set<number> | null,
  id: string | undefined,
  isEditMode: boolean
) {
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formData, setFormData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [originalData, setOriginalData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<number>>(
    initialSelectedFeatureIds || new Set()
  );
  const [originalSelectedFeatureIds, setOriginalSelectedFeatureIds] = useState<Set<number>>(
    initialSelectedFeatureIds || new Set()
  );
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [showError, showInfo]);

  // Sync with initial data when it changes
  useEffect(() => {
    if (initialFormData) {
      const currentSerialized = JSON.stringify(initialFormData);
      const lastSerialized = JSON.stringify(originalData);

      if (currentSerialized !== lastSerialized) {
        setFormData(initialFormData);
        setOriginalData(JSON.parse(JSON.stringify(initialFormData)));
        setErrors({});
      }
    } else if (!isEditMode && !initialFormData) {
      const defaultState = getDefaultFormState();
      setFormData(defaultState);
      setOriginalData(JSON.parse(JSON.stringify(defaultState)));
      setErrors({});
    }
  }, [initialFormData, isEditMode, originalData]);

  useEffect(() => {
    if (initialSelectedFeatureIds) {
      setSelectedFeatureIds(initialSelectedFeatureIds);
      setOriginalSelectedFeatureIds(new Set(initialSelectedFeatureIds));
    } else if (!isEditMode) {
      setSelectedFeatureIds(new Set());
      setOriginalSelectedFeatureIds(new Set());
    }
  }, [initialSelectedFeatureIds, isEditMode]);

  const hasChanges = useMemo(() => {
    if (!isEditMode) {
      return true;
    }

    const baseChanged =
      formData.name !== originalData.name ||
      formData.description !== originalData.description ||
      formData.enabled !== originalData.enabled;

    if (baseChanged) {
      return true;
    }

    if (selectedFeatureIds.size !== originalSelectedFeatureIds.size) {
      return true;
    }

    for (const featureId of selectedFeatureIds) {
      if (!originalSelectedFeatureIds.has(featureId)) {
        return true;
      }
    }

    return false;
  }, [formData, isEditMode, originalData, originalSelectedFeatureIds, selectedFeatureIds]);

  const handleChange = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [errors]
  );

  const moveToEnabled = useCallback((featureIds: number[]) => {
    if (featureIds.length === 0) {
      return;
    }

    setSelectedFeatureIds((prev) => {
      const next = new Set(prev);
      featureIds.forEach((featureId) => next.add(featureId));
      return next;
    });
  }, []);

  const moveToAvailable = useCallback((featureIds: number[]) => {
    if (featureIds.length === 0) {
      return;
    }

    setSelectedFeatureIds((prev) => {
      const next = new Set(prev);
      featureIds.forEach((featureId) => next.delete(featureId));
      return next;
    });
  }, []);

  const validateForm = useCallback(() => {
    const validationErrors = validateFeatureCategoryForm(formData, t);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [formData, t]);

  // Handle submit - Always-Active Save Standard: always attempt to save
  const handleSubmit = useCallback(async (): Promise<FormState & { id?: number; uuid?: string; selectedFeatureIds?: Set<number> } | undefined> => {
    if (!formData.name.trim()) {
      showErrorRef.current(t('featureCategories.validation.nameRequired'));
      return undefined;
    }

    if (!validateForm()) {
      return undefined;
    }

    setIsSaving(true);
    try {
      const result = isEditMode
        ? await updateFeatureCategory(id!, { ...formData, featureCodeIds: Array.from(selectedFeatureIds) }, t)
        : await createFeatureCategory({ ...formData, featureCodeIds: Array.from(selectedFeatureIds) }, t);

      showSuccess(
        isEditMode
          ? t('featureCategories.messages.updateSuccess')
          : t('featureCategories.messages.createSuccess')
      );

      const trimmedPayload: FormState = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        enabled: formData.enabled,
      };

      setOriginalData(trimmedPayload);
      setFormData(trimmedPayload);
      setOriginalSelectedFeatureIds(new Set(selectedFeatureIds));

      // Return data with ID and selectedFeatureIds for navigation
      return {
        ...trimmedPayload,
        id: result.data?.id,
        uuid: result.data?.uuid,
        selectedFeatureIds: new Set(selectedFeatureIds)
      } as any;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : isEditMode
            ? t('featureCategories.messages.updateError')
            : t('featureCategories.messages.createError');
      showErrorRef.current(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, id, isEditMode, selectedFeatureIds, showSuccess, t, validateForm]);

  const handleCancel = useCallback(() => {
    if (!isEditMode) {
      const reset = getDefaultFormState();
      setFormData(reset);
      setSelectedFeatureIds(new Set());
      setErrors({});
      return;
    }

    setFormData({ ...originalData });
    setSelectedFeatureIds(new Set(originalSelectedFeatureIds));
    setErrors({});
  }, [isEditMode, originalData, originalSelectedFeatureIds]);

  return {
    formData,
    setFormData,
    originalData,
    errors,
    isSaving,
    hasChanges,
    selectedFeatureIds,
    setSelectedFeatureIds,
    handleChange,
    moveToEnabled,
    moveToAvailable,
    handleSubmit,
    handleCancel,
    validateForm,
  };
}

